import { parseDateOnly, toDateOnlyString } from '@supply-mover-2-0/core';
import type React from 'react';
import { useTranslation } from '../../providers/TranslationProvider';
import { DatePicker } from './DatePicker';
import { Input } from './Input';
import { MaskedInput } from './MaskedInput';
import { MultiSelect } from './MultiSelect';
import { Select } from './Select';
import { TextArea } from './TextArea';
import { Toggle } from './Toggle';

/**
 * One input per field type, shared by custom field editing and Workd Forms
 * (docs/claude/forms/01-design.md §12), so a custom field and a form field linked to it
 * look and store values the same way: text as text, numbers and money as numbers, yes/no
 * as true/false, a date as YYYY-MM-DD, multi-select as a list of values.
 *
 * FormField clones this element to add `id` and the `aria-*` attributes; they are passed
 * on to the underlying native control where it takes them, and `aria-invalid` also turns
 * on the error style of the dropdown, date and money inputs.
 */
export interface FieldValueInputProps {
	/** A custom field type (text, number, select, …); an unknown type is plain text. */
	fieldType: string;
	value: unknown;
	onChange: (value: unknown) => void;
	onBlur?: () => void;
	/** Used as the multi-select's name and the fallback accessible name. */
	name: string;
	label?: string;
	selectOptions?: ReadonlyArray<{ value: string; label: string }> | null;
	placeholder?: string | null;
	maxLength?: number;
	min?: number;
	max?: number;
	disabled?: boolean;
	/**
	 * A cleared money field is "no value" rather than 0. Web forms need this so a required
	 * amount can be left blank and reported; custom field editing keeps its old 0.
	 */
	emptyMoneyIsNull?: boolean;
	/** An HTML autocomplete token, e.g. "email" or "organization". */
	autoComplete?: string;
	/**
	 * Show "Enter value" / "Enter number" when the field has no placeholder of its own.
	 * Staff screens want it; a public form shows only its own wording and format hints
	 * (email, phone, web address), since filler text in every box reads as noise there.
	 */
	genericPlaceholders?: boolean;
	// Injected by FormField.
	id?: string;
	'aria-invalid'?: boolean;
	'aria-describedby'?: string;
	'aria-required'?: boolean;
}

export function FieldValueInput({
	fieldType,
	value,
	onChange,
	onBlur,
	name,
	label,
	selectOptions,
	placeholder,
	maxLength,
	min,
	max,
	disabled = false,
	emptyMoneyIsNull = false,
	autoComplete,
	genericPlaceholders = true,
	id,
	'aria-invalid': ariaInvalid,
	'aria-describedby': ariaDescribedBy,
	'aria-required': ariaRequired,
}: FieldValueInputProps): React.ReactElement {
	const { t } = useTranslation();
	const native = {
		id,
		name,
		'aria-invalid': ariaInvalid,
		'aria-describedby': ariaDescribedBy,
		'aria-required': ariaRequired,
		autoComplete,
		disabled,
		onBlur,
	};
	const text =
		typeof value === 'string' || typeof value === 'number' ? String(value) : '';
	const placeholderOr = (fallback: string): string =>
		placeholder != null && placeholder !== '' ? t(placeholder) : t(fallback);
	const genericPlaceholderOr = (fallback: string): string | undefined =>
		genericPlaceholders || (placeholder != null && placeholder !== '')
			? placeholderOr(fallback)
			: undefined;

	switch (fieldType) {
		case 'textarea':
			return (
				<TextArea
					{...native}
					value={text}
					onChange={(event) => {
						onChange(event.target.value === '' ? null : event.target.value);
					}}
					placeholder={genericPlaceholderOr('Enter value')}
					maxLength={maxLength}
					rows={4}
				/>
			);

		case 'number':
			return (
				<Input
					{...native}
					type="number"
					inputMode="decimal"
					value={text}
					onChange={(event) => {
						onChange(
							event.target.value === '' ? null : Number(event.target.value),
						);
					}}
					placeholder={genericPlaceholderOr('Enter number')}
					min={min}
					max={max}
				/>
			);

		case 'currency':
			return (
				<MaskedInput
					mask="currency"
					value={
						typeof value === 'number' ? value : emptyMoneyIsNull ? null : 0
					}
					nullable={emptyMoneyIsNull}
					onChange={(amount) => {
						onChange(amount);
					}}
					onClear={() => {
						onChange(null);
					}}
					onBlur={onBlur}
					placeholder={placeholder ?? undefined}
					error={ariaInvalid === true}
					disabled={disabled}
				/>
			);

		case 'date':
			// Stored as YYYY-MM-DD, the form the shared validator accepts. The picker works in
			// local-midnight dates, so convert both ways without passing through UTC (which
			// would move the day for anyone behind or ahead of it).
			return (
				<DatePicker
					selected={parseDateOnly(typeof value === 'string' ? value : null)}
					onChange={(date) => {
						const day = toDateOnlyString(date);
						onChange(day === '' ? null : day);
						onBlur?.();
					}}
					disabled={disabled}
					error={ariaInvalid === true}
					allowClear
				/>
			);

		case 'datetime':
			return (
				<Input
					{...native}
					type="datetime-local"
					value={text}
					onChange={(event) => {
						onChange(event.target.value === '' ? null : event.target.value);
					}}
				/>
			);

		case 'boolean':
			return (
				<Toggle
					id={id}
					checked={value === true}
					onChange={(checked) => {
						onChange(checked);
						onBlur?.();
					}}
					disabled={disabled}
				/>
			);

		case 'select':
			if (selectOptions == null || selectOptions.length === 0) {
				return (
					<div className="text-sm text-subtle">
						{t('No options configured')}
					</div>
				);
			}
			return (
				<Select
					name={name}
					value={typeof value === 'string' ? value : ''}
					onChange={(choice) => {
						onChange(choice === '' ? null : choice);
						onBlur?.();
					}}
					options={selectOptions.map((option) => ({
						value: option.value,
						label: t(option.label),
					}))}
					placeholder={placeholderOr('Select...')}
					aria-label={label != null ? t(label) : undefined}
					error={ariaInvalid === true}
					disabled={disabled}
					allowClear
				/>
			);

		case 'multiselect': {
			if (selectOptions == null || selectOptions.length === 0) {
				return (
					<div className="text-sm text-subtle">
						{t('No options configured')}
					</div>
				);
			}
			// Stored as a list of option values; MultiSelect works in option objects, so map
			// on the way in and back to values on the way out.
			const chosen = Array.isArray(value) ? (value as Array<unknown>) : [];
			const options = selectOptions.map((option) => ({
				value: option.value,
				label: t(option.label),
			}));
			return (
				<MultiSelect
					name={name}
					value={options.filter((option) => chosen.includes(option.value))}
					onChange={(choices) => {
						onChange(choices.map((choice) => String(choice.value)));
						onBlur?.();
					}}
					options={options}
					placeholder={placeholderOr('Select...')}
					isDisabled={disabled}
				/>
			);
		}

		case 'phone':
			return (
				<Input
					{...native}
					type="tel"
					inputMode="tel"
					value={text}
					onChange={(event) => {
						onChange(event.target.value === '' ? null : event.target.value);
					}}
					placeholder={placeholderOr('(555) 555-5555')}
					maxLength={maxLength}
				/>
			);

		case 'email':
			return (
				<Input
					{...native}
					type="email"
					inputMode="email"
					value={text}
					onChange={(event) => {
						onChange(event.target.value === '' ? null : event.target.value);
					}}
					placeholder={placeholderOr('email@example.com')}
					maxLength={maxLength}
				/>
			);

		case 'url':
			return (
				<Input
					{...native}
					type="url"
					inputMode="url"
					value={text}
					onChange={(event) => {
						onChange(event.target.value === '' ? null : event.target.value);
					}}
					placeholder={placeholderOr('https://...')}
					maxLength={maxLength}
				/>
			);

		default:
			return (
				<Input
					{...native}
					value={text}
					onChange={(event) => {
						onChange(event.target.value === '' ? null : event.target.value);
					}}
					placeholder={genericPlaceholderOr('Enter value')}
					maxLength={maxLength}
				/>
			);
	}
}
